import type { MaybeHTMLInputElement, ObjectTabbable } from './types';
import { find as findFocusable } from './focusable';

/**
 * Returns the tab index of the given element. In contrast with the tabIndex
 * property, this normalizes the default (0) to avoid browser inconsistencies,
 * operating under the assumption that this function is only ever called with a
 * focusable node.
 *
 * @see https://bugzilla.mozilla.org/show_bug.cgi?id=1190261
 *
 * @param element Element from which to retrieve.
 *
 * @return Tab index of element (default 0).
 */
function getTabIndex( element: Element ): number {
	const tabIndex = element.getAttribute( 'tabindex' );
	return tabIndex === null ? 0 : parseInt( tabIndex, 10 );
}

/**
 * Returns true if the specified element is tabbable, or false otherwise.
 *
 * @param element Element to test.
 *
 * @return Whether element is tabbable.
 */
export function isTabbableIndex( element: Element ): boolean {
	return getTabIndex( element ) !== -1;
}

/**
 * Returns a stateful reducer function which constructs a filtered array of
 * tabbable elements, where at most one radio input is selected for a given
 * name, giving priority to checked input, falling back to the first
 * encountered.
 *
 * @return Radio group collapse reducer.
 */
function createStatefulCollapseRadioGroup(): (
	acc: MaybeHTMLInputElement[],
	el: MaybeHTMLInputElement
) => MaybeHTMLInputElement[] {
	const CHOSEN_RADIO_BY_NAME: Record< string, MaybeHTMLInputElement > = {};

	return function collapseRadioGroup(
		result: MaybeHTMLInputElement[],
		element: MaybeHTMLInputElement
	) {
		const { nodeName, type, checked, name } = element;

		// For all non-radio tabbables, construct to array by concatenating.
		if ( nodeName !== 'INPUT' || type !== 'radio' || ! name ) {
			return result.concat( element );
		}

		const hasChosen = CHOSEN_RADIO_BY_NAME.hasOwnProperty( name );

		// Omit by skipping concatenation if the radio element is not chosen.
		const isChosen = checked || ! hasChosen;
		if ( ! isChosen ) {
			return result;
		}

		// At this point, if there had been a chosen element, the current
		// element is checked and should take priority. Retroactively remove
		// the element which had previously been considered the chosen one.
		if ( hasChosen ) {
			const hadChosenElement = CHOSEN_RADIO_BY_NAME[ name ];
			result = result.filter( ( e ) => e !== hadChosenElement );
		}

		CHOSEN_RADIO_BY_NAME[ name ] = element;

		return result.concat( element );
	};
}

/**
 * An array map callback, returning an object with the element value and its
 * array index location as properties. This is used to emulate a proper stable
 * sort where equal tabIndex should be left in order of their occurrence in the
 * document.
 *
 * @param element Element.
 * @param index   Array index of element.
 *
 * @return Mapped object with element, index.
 */
function mapElementToObjectTabbable(
	element: HTMLElement,
	index: number
): ObjectTabbable {
	return { element, index };
}

/**
 * An array map callback, returning an element of the given mapped object's
 * element value.
 *
 * @param object Mapped object with element.
 *
 * @return Mapped object element.
 */
function mapObjectTabbableToElement(
	object: Pick< ObjectTabbable, 'element' >
): HTMLElement {
	return object.element;
}

/**
 * A sort comparator function used in comparing two objects of mapped elements.
 *
 * @see mapElementToObjectTabbable
 *
 * @param a First object to compare.
 * @param b Second object to compare.
 *
 * @return Comparator result.
 */
function compareObjectTabbables(
	a: ObjectTabbable,
	b: ObjectTabbable
): number {
	const aTabIndex = getTabIndex( a.element );
	const bTabIndex = getTabIndex( b.element );

	if ( aTabIndex === bTabIndex ) {
		return a.index - b.index;
	}

	return aTabIndex - bTabIndex;
}

/**
 * Given focusable elements, filters out tabbable element.
 *
 * @param focusables Focusable elements to filter.
 *
 * @return Tabbable elements.
 */
function filterTabbable( focusables: HTMLElement[] ): HTMLElement[] {
	return focusables
		.filter( isTabbableIndex )
		.map( mapElementToObjectTabbable )
		.sort( compareObjectTabbables )
		.map( mapObjectTabbableToElement )
		.reduce( createStatefulCollapseRadioGroup(), [] );
}

/**
 * @param context
 * @return Tabbable elements within the context.
 */
export function find( context: Element ): HTMLElement[] {
	return filterTabbable( findFocusable( context ) );
}

/**
 * Given a focusable element, find the preceding tabbable element.
 *
 * @param element The focusable element before which to look. Defaults
 *                to the active element.
 *
 * @return Preceding tabbable element.
 */
export function findPrevious( element: Element ): HTMLElement | undefined {
	return filterTabbable( findFocusable( element.ownerDocument.body ) )
		.reverse()
		.find(
			( focusable ) =>
				// eslint-disable-next-line no-bitwise
				element.compareDocumentPosition( focusable ) &
				element.DOCUMENT_POSITION_PRECEDING
		);
}

/**
 * Given a focusable element, find the next tabbable element.
 *
 * @param element The focusable element after which to look. Defaults
 *                to the active element.
 *
 * @return Next tabbable element.
 */
export function findNext( element: Element ): HTMLElement | undefined {
	return filterTabbable( findFocusable( element.ownerDocument.body ) ).find(
		( focusable ) =>
			// eslint-disable-next-line no-bitwise
			element.compareDocumentPosition( focusable ) &
			element.DOCUMENT_POSITION_FOLLOWING
	);
}
